function createError(response, data) {
    const error = new Error(response.statusText || 'Request failed')
    error.response = {
        status: response.status,
        statusText: response.statusText,
        headers: parseHeaders(response.headers),
        data,
    }
    error.status = response.status
    error.headers = error.response.headers
    error.data = data
    return error
}

function getFetchImplementation() {
    if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
        return window.fetch.bind(window)
    }

    if (typeof global !== 'undefined' && typeof global.fetch === 'function') {
        return global.fetch.bind(global)
    }

    throw new ReferenceError('fetch is not defined')
}

function parseHeaders(headers) {
    const result = {}
    headers.forEach((value, key) => {
        result[key] = value
    })
    return result
}

function buildBody(body, headers = {}) {
    if (body === null || typeof body === 'undefined') return undefined
    if (body instanceof FormData || body instanceof URLSearchParams || typeof body === 'string') return body

    if ((headers['Content-Type'] || headers['content-type']) === 'application/json') {
        return JSON.stringify(body)
    }

    return body
}

function buildUrl(baseUrl, path, query = null) {
    const url = /^https?:\/\//.test(path)
        ? new URL(path)
        : new URL(path.replace(/^\//, ''), `${baseUrl.replace(/\/?$/, '/')}`)

    if (query) {
        Object.entries(query).forEach(([key, value]) => {
            if (value || value === 0) {
                url.searchParams.set(key, value)
            }
        })
    }

    return url.toString()
}

function withTimeout(timeout, signal) {
    if (!timeout && !signal) {
        return { signal: undefined, cleanup: () => {} }
    }

    const controller = new AbortController()
    let timeoutId = null

    if (signal) {
        if (signal.aborted) {
            controller.abort()
        } else {
            signal.addEventListener('abort', () => controller.abort(), { once: true })
        }
    }

    if (timeout) {
        timeoutId = setTimeout(() => controller.abort(), timeout)
    }

    return {
        signal: controller.signal,
        cleanup: () => timeoutId && clearTimeout(timeoutId),
    }
}

export function createAbortController() {
    return new AbortController()
}

export default function createHttpClient({ baseUrl, timeout = 10000, headers = {}, getToken } = {}) {
    const fetchImpl = getFetchImplementation()

    async function parseResponseBody(response) {
        const contentType = response.headers.get('content-type') || ''

        if (!contentType.includes('application/json')) {
            return response.text()
        }

        const fallbackResponse = typeof response.clone === 'function' ? response.clone() : response

        try {
            return await response.json()
        } catch (error) {
            return typeof fallbackResponse.text === 'function' ? fallbackResponse.text() : ''
        }
    }

    async function request(method, path, { query, body, headers: extraHeaders = {}, signal, timeout: requestTimeout } = {}) {
        const mergedHeaders = {
            ...headers,
            ...(getToken && getToken() ? { Authorization: getToken() } : {}),
            ...extraHeaders,
        }
        const timeoutContext = withTimeout(requestTimeout || timeout, signal)

        try {
            const response = await fetchImpl(buildUrl(baseUrl, path, query), {
                method,
                headers: mergedHeaders,
                signal: timeoutContext.signal,
                body: ['GET', 'DELETE'].includes(method) ? undefined : buildBody(body, mergedHeaders),
            })

            const responseBody = await parseResponseBody(response)

            if (!response.ok) {
                throw createError(response, responseBody)
            }

            return {
                status: response.status,
                headers: parseHeaders(response.headers),
                body: responseBody,
            }
        } catch (error) {
            if (error.name === 'AbortError' && !(signal && signal.aborted)) {
                throw new Error(`timeout of ${requestTimeout || timeout}ms exceeded`)
            }

            throw error
        } finally {
            timeoutContext.cleanup()
        }
    }

    return {
        get(path, options) {
            return request('GET', path, options)
        },
        post(path, body, options = {}) {
            return request('POST', path, { ...options, body })
        },
        put(path, body, options = {}) {
            return request('PUT', path, { ...options, body })
        },
        delete(path, options) {
            return request('DELETE', path, options)
        },
    }
}
